import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deleteInboxItem, listInboxItems, type InboxItem } from "../api/inboxApi";
import { toast } from "react-toastify";
import { AssignInboxItemDialog } from "./AssignInboxItemDialog";
import { QuickAddDialog } from "./QuickAddDialog";

function formatInboxDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function InboxPage() {
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [assignItem, setAssignItem] = useState<InboxItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<InboxItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listInboxItems();
      setItems(rows);
      window.dispatchEvent(new CustomEvent("inbox-updated"));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load inbox";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const removeItemFromList = useCallback((uid: string) => {
    setItems((current) => current.filter((item) => item.uid !== uid));
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteInboxItem(deleteItem.uid);
      removeItemFromList(deleteItem.uid);
      window.dispatchEvent(new CustomEvent("inbox-updated"));
      toast.success("Inbox item deleted");
      setDeleteItem(null);
    } catch (err: unknown) {
      const errorObj = err as Error & { status?: number };
      if (errorObj.status === 404) {
        removeItemFromList(deleteItem.uid);
        setDeleteItem(null);
        return;
      }
      toast.error(errorObj.message || "Could not delete item");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Inbox</h1>
          <p className="text-sm text-muted-foreground">
            Quick captures waiting to be moved into a collection.
          </p>
        </div>
        <Button type="button" onClick={() => setQuickAddOpen(true)}>
          Quick Add
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading inbox...</p>
      ) : null}

      {error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-red-600">{error}</p>
            <Button type="button" variant="outline" className="mt-3" onClick={() => void loadItems()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Nothing in your inbox.</p>
            <Button type="button" onClick={() => setQuickAddOpen(true)}>
              Quick add something
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item.uid}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-normal text-muted-foreground">
                  {formatInboxDate(item.createdAt)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="whitespace-pre-wrap break-words">{item.text}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={() => setAssignItem(item)}>
                    Assign
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setDeleteItem(item)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <QuickAddDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onSavedToInbox={() => void loadItems()}
      />

      <AssignInboxItemDialog
        open={Boolean(assignItem)}
        onOpenChange={(open) => {
          if (!open) setAssignItem(null);
        }}
        inboxItem={assignItem}
        onAssigned={() => {
          if (assignItem) removeItemFromList(assignItem.uid);
          setAssignItem(null);
          window.dispatchEvent(new CustomEvent("inbox-updated"));
        }}
        onStaleItem={() => {
          if (assignItem) removeItemFromList(assignItem.uid);
          setAssignItem(null);
        }}
      />

      <Dialog open={Boolean(deleteItem)} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete inbox item?</DialogTitle>
            <DialogDescription>
              This will permanently remove the item from your inbox.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteItem(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void handleDeleteConfirm()} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
